import { useState } from "react";
import { validateBible } from "../domain/validate";
import { diffBibles, summarizeDiff } from "../domain/diff";
import { applyImport, createBackup, exportUrl, fetchRevisions, previewImport } from "../persistence/api";
import { useStore } from "../store";
import type { Bible } from "../domain/types";

export function Settings() {
  const { bible, revisionNumber, resetToSeed, reloadRemote, dirty } = useStore();
  const [revisions, setRevisions] = useState<Array<{ revision_number: number; created_at: string; summary: string }>>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<Bible | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const onFile = async (file: File) => {
    const text = await file.text();
    let data: unknown;
    try {
      data = JSON.parse(text);
    } catch {
      setMsg("JSON illisible.");
      return;
    }
    const errors = validateBible(data);
    if (errors.length) {
      setMsg("Import refusé :\n" + errors.map((e) => `${e.path}: ${e.message}`).join("\n"));
      setIncoming(null);
      return;
    }
    const next = data as Bible;
    setIncoming(next);
    if (bible) {
      const ops = diffBibles(bible, next);
      setPreview(summarizeDiff(ops) + "\n" + ops.slice(0, 30).map((o) => `${o.operation} ${o.entityType} ${o.entityId}`).join("\n"));
    }
    try {
      await previewImport(next);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Prévisualisation serveur refusée.");
    }
  };

  return (
    <div className="grid">
      <h1>Paramètres et sauvegardes</h1>
      <section className="card">
        <h2>Flux hybride</h2>
        <ol className="tiny">
          <li>Cursor édite <code>data/*.json</code> puis <code>npm run assemble</code> et <code>npm run validate</code>.</li>
          <li>Le site charge MySQL (amorcé depuis <code>data/bible.json</code>).</li>
          <li>Les modifications du site s’enregistrent en révision distante + brouillon IndexedDB.</li>
          <li>Export JSON → remplacement contrôlé des fichiers après aperçu.</li>
        </ol>
      </section>
      <section className="card">
        <h2>Export / import</h2>
        <div className="row">
          <a className="btn primary" href={exportUrl()}>Télécharger la Bible</a>
          <button className="btn" type="button" onClick={() => void createBackup()}>Sauvegarde serveur</button>
        </div>
        <p className="tiny">Révision courante : {revisionNumber}{dirty ? " · brouillon local non poussé" : ""}</p>
        <label className="field">
          <span>Importer un JSON (aperçu d’abord)</span>
          <input type="file" accept="application/json" onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }} />
        </label>
        {preview && <pre className="context">{preview}</pre>}
        {incoming && bible && (
          <button
            className="btn danger"
            type="button"
            onClick={async () => {
              if (!confirm("Remplacer la Bible distante par ce fichier ?")) return;
              try {
                await applyImport(incoming, revisionNumber);
                await reloadRemote();
                setMsg("Import appliqué.");
                setIncoming(null);
              } catch (e) {
                setMsg(e instanceof Error ? e.message : "Import refusé.");
              }
            }}
          >
            Confirmer l’import
          </button>
        )}
        {msg && <pre className="context">{msg}</pre>}
      </section>
      <section className="card">
        <h2>Révisions</h2>
        <button className="btn" type="button" onClick={async () => setRevisions((await fetchRevisions()).revisions)}>
          Charger l’historique
        </button>
        <ul>
          {revisions.map((r) => (
            <li key={r.revision_number} className="tiny">
              #{r.revision_number} · {r.created_at} · {r.summary}
            </li>
          ))}
        </ul>
      </section>
      <section className="card">
        <h2>Réinitialiser vers les fichiers sources</h2>
        <p className="tiny">Crée une sauvegarde puis recharge <code>data/bible.json</code>. Irréversible sans cette sauvegarde.</p>
        <button
          className="btn danger"
          type="button"
          onClick={async () => {
            if (!confirm("Réinitialiser depuis data/bible.json ? Une sauvegarde sera créée.")) return;
            await resetToSeed();
          }}
        >
          Réinitialiser
        </button>
      </section>
    </div>
  );
}
